import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";
// React Native's own ScrollView: Tamagui's styled one drops `onScroll` on
// native (it is in Tamagui's web-only prop list).
import { type LayoutChangeEvent, type NativeScrollEvent, type NativeSyntheticEvent, ScrollView, type ScrollViewProps } from "react-native";
import { type GetProps, getTokenValue, XStack, YStack } from "tamagui";
import { LinearGradient } from "@tamagui/linear-gradient";
import { withAlpha } from "@medbillai/utils/color";
type Axis = "horizontal" | "vertical";
type ColorToken = Exclude<Parameters<typeof getTokenValue>[0], "unset" | "auto">;
type ScrollMetrics = {
  offset: number;
  viewport: number;
  content: number;
};

/** A span along the scroll axis, in content coordinates. */
export type ScrollRange = {
  start: number;
  end: number;
};

// Absorbs subpixel sizes so the fades don't flicker at either end.
const EDGE_THRESHOLD = 1;

/** Which ends have content beyond them. Exported for its test only. */
export const scrollEdges = ({
  offset,
  viewport,
  content
}: ScrollMetrics) => ({
  atStart: offset <= EDGE_THRESHOLD,
  atEnd: offset + viewport >= content - EDGE_THRESHOLD
});

/**
 * The offset that shows all of `range` clear of a `margin`-wide fade, or null
 * when it is already in view. Exported for its test only.
 */
export const revealOffset = ({
  offset,
  viewport,
  content
}: ScrollMetrics, range: ScrollRange, margin: number) => {
  const max = Math.max(0, content - viewport);
  const target = range.start - margin < offset ? range.start - margin : range.end + margin > offset + viewport ? range.end + margin - viewport : offset;
  const clamped = Math.min(max, Math.max(0, target));
  return Math.abs(clamped - offset) > EDGE_THRESHOLD ? clamped : null;
};
const useScrollMetrics = (axis: Axis) => {
  const [metrics, setMetrics] = useState<ScrollMetrics>({
    offset: 0,
    viewport: 0,
    content: 0
  });
  const handlers = useMemo(() => {
    const vertical = axis === "vertical";
    const length = (event: LayoutChangeEvent) => vertical ? event.nativeEvent.layout.height : event.nativeEvent.layout.width;
    return {
      onViewportLayout: (event: LayoutChangeEvent) => {
        const viewport = length(event);
        setMetrics(current => ({
          ...current,
          viewport
        }));
      },
      onContentLayout: (event: LayoutChangeEvent) => {
        const content = length(event);
        setMetrics(current => ({
          ...current,
          content
        }));
      },
      onScroll: (event: NativeSyntheticEvent<NativeScrollEvent>) => {
        const {
          contentOffset,
          contentSize,
          layoutMeasurement
        } = event.nativeEvent;
        setMetrics({
          offset: vertical ? contentOffset.y : contentOffset.x,
          viewport: vertical ? layoutMeasurement.height : layoutMeasurement.width,
          content: vertical ? contentSize.height : contentSize.width
        });
      }
    };
  }, [axis]);
  return {
    metrics,
    ...handlers
  };
};
const FADE_SIZE = {
  horizontal: "$si6",
  vertical: "$si4"
} as const;
const fadePlacement = {
  horizontal: {
    start: {
      left: 0,
      top: 0,
      width: FADE_SIZE.horizontal
    },
    end: {
      right: 0,
      top: 0,
      width: FADE_SIZE.horizontal
    }
  },
  vertical: {
    start: {
      top: 0,
      left: 0,
      right: 0,
      height: FADE_SIZE.vertical
    },
    end: {
      bottom: 0,
      left: 0,
      right: 0,
      height: FADE_SIZE.vertical
    }
  }
} as const;
type FadingScrollViewProps = Pick<ScrollViewProps, "keyboardShouldPersistTaps" | "onLayout" | "onScroll" | "scrollEventThrottle" | "showsHorizontalScrollIndicator" | "showsVerticalScrollIndicator" | "testID"> & Pick<GetProps<typeof YStack>, "flex" | "height" | "maxHeight"> & {
  axis: Axis;
  /** The surface the content scrolls over; each fade lands on this colour. */
  background: ColorToken;
  /** Lifts horizontal fades off the bottom edge, e.g. to clear an underline. */
  fadeInset?: GetProps<typeof YStack>["bottom"];
  /** Scrolls this span fully into view, clear of the fades, whenever it changes. */
  revealRange?: ScrollRange | null;
  children: ReactNode;
};

/**
 * A ScrollView that fades whichever edge has more content past it: the end
 * fade until scrolled to the end, the start fade once scrolled away from it,
 * neither when everything fits. Size it with `flex`, `height` or `maxHeight`.
 */
export const FadingScrollView = ({
  axis,
  background,
  fadeInset = 0,
  revealRange,
  flex,
  height,
  maxHeight,
  onLayout,
  onScroll,
  children,
  ...rest
}: FadingScrollViewProps) => {
  const scroll = useScrollMetrics(axis);
  const edges = scrollEdges(scroll.metrics);
  const scrollRef = useRef<ScrollView>(null);
  const metricsRef = useRef(scroll.metrics);
  metricsRef.current = scroll.metrics;
  const horizontal = axis === "horizontal";
  const revealStart = revealRange?.start;
  const revealEnd = revealRange?.end;
  const {
    viewport,
    content
  } = scroll.metrics;
  const revealed = useRef<string | null>(null);
  useEffect(() => {
    if (revealStart === undefined || revealEnd === undefined || viewport <= 0 || content <= 0) {
      return;
    }
    const key = `${revealStart}:${revealEnd}`;
    if (revealed.current === key) {
      return;
    }
    revealed.current = key;
    const margin = Number(getTokenValue(FADE_SIZE[axis], "size"));
    const target = revealOffset({
      offset: metricsRef.current.offset,
      viewport,
      content
    }, {
      start: revealStart,
      end: revealEnd
    }, margin);
    if (target !== null) {
      scrollRef.current?.scrollTo(horizontal ? {
        x: target,
        animated: true
      } : {
        y: target,
        animated: true
      });
    }
  }, [axis, horizontal, revealStart, revealEnd, viewport, content]);
  const color = String(getTokenValue(background, "color"));
  // Native fades "transparent" through black; fade through the colour.
  const clear = withAlpha(color, 0);
  const Content = horizontal ? XStack : YStack;
  const fade = (side: "start" | "end") => <LinearGradient aria-hidden pointerEvents="none" position="absolute" {...fadePlacement[axis][side]} {...horizontal ? {
    bottom: fadeInset
  } : {}} start={[0, 0]} end={horizontal ? [1, 0] : [0, 1]} colors={side === "end" ? [clear, color] : [color, clear]} data-sentry-element="LinearGradient" data-sentry-component="fade" data-sentry-source-file="FadingScrollView.tsx" />;
  return <YStack position="relative" flex={flex} height={height} maxHeight={maxHeight} data-sentry-element="YStack" data-sentry-component="FadingScrollView" data-sentry-source-file="FadingScrollView.tsx">
      <ScrollView ref={scrollRef} horizontal={horizontal} style={{
      flexGrow: 1,
      flexShrink: 1
    }} scrollEventThrottle={16} {...rest} onLayout={event => {
      scroll.onViewportLayout(event);
      onLayout?.(event);
    }} onScroll={event => {
      scroll.onScroll(event);
      onScroll?.(event);
    }} data-sentry-element="ScrollView" data-sentry-source-file="FadingScrollView.tsx">
        {/* Measured before any scroll: on web a horizontal content container shrinks to the viewport. */}
        <Content onLayout={scroll.onContentLayout} data-sentry-element="Content" data-sentry-source-file="FadingScrollView.tsx">{children}</Content>
      </ScrollView>
      {edges.atStart ? null : fade("start")}
      {edges.atEnd ? null : fade("end")}
    </YStack>;
};